<template>
  <div
    class="login-section"
    :class="{ 'login-section--mode-edit': loginType !== 'login' && loginType !== 'notification' }"
  >
    <div class="login-section__input-area">
      <ValidationObserver ref="observer" tag="div">
        <div class="login-section__input-item">
          <ValidationProvider name="email" rules="required" v-slot="{ errors }" tag="div">
            <atom-input-e-mail
              class="login-section__input-area__email-field"
              :class="{
                'login-section__input-area__email-field--validation-error': showLoginError,
              }"
              :id="'email'"
              :placeholder="$tc('placeholder.email')"
              :error="showLoginError"
              v-model="loginSectionState.loginName"
              @enter="login"
            />
            <error-message-parts
              class="login-section__input-area__error-message"
              :showError="errors.length > 0"
              :message="errors[0]"
            />
          </ValidationProvider>
        </div>

        <div class="login-section__input-item">
          <ValidationProvider name="password" rules="required" v-slot="{ errors }" tag="div">
            <password-with-toggle-button-parts
              class="login-section__input-area__pw-field"
              :class="{ 'login-section__input-area__pw-field--validation-error': showLoginError }"
              :id="'password'"
              v-model="loginSectionState.password"
              :placeholder="$tc('placeholder.password')"
              :error="showLoginError"
              @enter="login"
            />
            <error-message-parts
              class="login-section__input-area__error-message"
              :showError="showLoginError || errors.length > 0"
              :message="errors[0] || $tc('LoginPage.loginError')"
            />
            <error-message-parts
              class="login-section__input-area__error-message"
              :showError="showNetworkError"
              :message="`${$tc('LoginPage.networkError')}`"
            />
          </ValidationProvider>
        </div>
      </ValidationObserver>
      <p class="login-section__input-area__forgot">
        <a class="login-section__input-area__forgot__link" :href="passwordResetUrl">{{
          $tc('LoginPage.forgetPassword')
        }}</a>
      </p>
    </div>

    <div class="login-section__buttons">
      <atom-input-button
        class="login-section__login-btn"
        :linkText="submitText"
        :isDisabled="isProcessingLogin"
        :theme="submitButtonTheme"
        colorType="primary"
        @push="login"
      />

      <div
        v-if="loginType === 'login' || loginType === 'notification'"
        class="login-section__signup"
      >
        <ul class="login-section__signup__list">
          <li class="login-section__signup__item">
            <atom-router-button
              v-if="isApp"
              to="/signup"
              :isOpenAsBlank="isIosDevice"
              :linkText="signupButtonLabel"
              :theme="submitButtonTheme"
              colorType="secondary"
            />
            <atom-link-button
              v-else
              :url="signupUrl"
              :isOpenAsBlank="isIosDevice"
              :linkText="signupButtonLabel"
              :theme="submitButtonTheme"
              colorType="secondary"
            />
          </li>
          <li v-if="isApp" class="login-section__signup__item">
            <atom-router-button
              to="/signup/one-time-pass"
              :linkText="$tc('LoginPage.signupLabelForOneTimePass')"
              :theme="submitButtonTheme"
              colorType="secondary"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive } from '@vue/composition-api'
import { ValidationObserver } from 'vee-validate'
import I18n from '@/locales/I18n'
import PasswordWithToggleButtonParts from '@/components/common/form/PasswordWithToggleButtonParts.vue'
import ErrorMessageParts from '@/components/LoginPage/LoginPane/parts/ErrorMessageParts.vue'
import AtomInputEMail from '@/components/atoms/input/AtomInputEMail.vue'
import AtomInputButton from '@/components/atoms/input/AtomInputButton.vue'
import SecureKeyStore from '@/util/secureKeyStore/SecureKeyStore'
import LocalStorageAccessor from '@/util/localstorage/LocalStorageAccessor'
import { LoginType } from '@/components/LoginPage/LoginPane.vue'
import DeviceInfo from '@/util/DeviceInfo'
import AtomRouterButton from '@/components/atoms/AtomRouterButton.vue'
import AtomLinkButton from '@/components/atoms/AtomLinkButton.vue'

/**
 * ログイン画面のログインフィールドセクション。
 */
export default defineComponent({
  name: 'LoginSection',
  components: {
    AtomLinkButton,
    AtomRouterButton,
    AtomInputButton,
    AtomInputEMail,
    PasswordWithToggleButtonParts,
    ErrorMessageParts,
  },
  props: {
    /**
     * ログインタイプ指定
     */
    loginType: {
      type: String as PropType<LoginType>,
      default: 'login',
    },
    /**
     * ログインエラーメッセージを表示するかどうか
     */
    showLoginError: {
      type: Boolean,
      default: false,
    },
    /**
     * ログイン時にネットワークエラーが発生したかどうか
     */
    showNetworkError: {
      type: Boolean,
      default: false,
    },
    /**
     * ログイン処理中かどうか
     */
    isProcessingLogin: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    /** 入力中のログインIDとパスワード */
    const loginSectionState = reactive({
      loginName: '',
      password: '',
    })

    /**
     * セキュアキーストアに保存されている認証情報を取得する。
     * @return credentialType 認証情報
     */
    const loadCredentials = async () => {
      const credential = await SecureKeyStore.getItem('sfgoUserCredential')
      if (credential) {
        loginSectionState.loginName = credential.loginId
        loginSectionState.password = credential.password
      }
      return credential
    }

    const submitText =
      props.loginType === 'login' || props.loginType === 'notification'
        ? I18n.tc('LoginPage.login')
        : I18n.tc('MypagePage.Login')
    const submitButtonTheme =
      props.loginType === 'login' || props.loginType === 'notification' ? 'dark' : 'light'

    /** パスワードリセット画面への遷移設定 */
    const passwordResetUrl = computed(
      () =>
        `${process.env.VUE_APP_BROWSER_BASE_URL}#/password-reset/${
          DeviceInfo.isCordova() ? 'returnApp' : ''
        }`,
    )

    /** SFgo-1040：iOSアプリの場合、ログイン画面から新規会員登録を無くす対応 */
    const isIosDevice = computed(() => DeviceInfo.isiOS())

    const isApp = DeviceInfo.isCordova()

    /** 新規登録画面への遷移設定 */
    const signupUrl = computed(() => `${process.env.VUE_APP_BROWSER_BASE_URL}#/signup/free`)

    /** 新規会員登録ボタンラベルテキスト */
    const signupButtonLabel = computed(() => I18n.tc('LoginPage.signup'))

    return {
      loginSectionState,
      loadCredentials,
      submitText,
      submitButtonTheme,
      passwordResetUrl,
      signupButtonLabel,
      signupUrl,
      isIosDevice,
      isApp,
    }
  },
  watch: {
    loginSectionState: {
      /**
       * ログインIDとパスワードの変更を検知する。
       * ログインIDもしくはパスワードが更新されたら発火する。
       */
      handler() {
        this.$emit('input-change')
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      if ((await this.loadCredentials()) && !LocalStorageAccessor.transitionFromLogout) {
        // 認証情報を読み込めたら自動ログインする。ただし、ログアウトから遷移してきた場合は自動ログインしない
        this.login()
      }
    } finally {
      LocalStorageAccessor.transitionFromLogout = false
    }
  },
  methods: {
    /**
     * ログインする
     */
    async login() {
      const currentHost = window.location.hostname
      const appRedirectUrl = process.env.VUE_APP_APP_REDIRECT_URL
      const browserUrl = process.env.VUE_APP_BROWSER_CAN_REDIRECT_TO_APP_BASE_URL
      const isForPC = DeviceInfo.isForPC()
      /**
       * VUE_APP_APP_REDIRECT_URLでアクセスされている場合、
       * VUE_APP_BROWSER_CAN_REDIRECT_TO_APP_BASE_URL にリダイレクトする
       * ※VUE_APP_APP_REDIRECT_URLではGoogle認証コールバック時のみ使用し、通常ログインはできないようにする
       */
      if (isForPC && appRedirectUrl && appRedirectUrl.includes(currentHost)) {
        window.location.href = browserUrl
        return
      }

      // バリデーションを実行する
      const observer = this.$refs.observer as InstanceType<typeof ValidationObserver>
      const isValid = await observer?.validate()
      if (!isValid) {
        return
      }
      /**
       * loginイベントを発火する
       * @event login
       * @property { reactive<{ loginName: string; password: string; }> } ログインIDとパスワード
       */
      this.$emit('login', this.loginSectionState)
    },
  },
})
</script>

<style lang="scss" scoped>
.login-section {
  width: 100%;
  padding: 0 min(#{convertPXtoVW(14)}, 14px);

  .login-section__input-area {
    width: 100%;
    text-align: left;

    &__email-field {
      backdrop-filter: blur(12px);
    }

    &__forgot,
    &__signup {
      text-align: center;

      &__link {
        font-size: 12px;
        font-weight: 700;
        text-decoration: underline;
        text-underline-offset: 5px;
        color: white;
      }
    }

    &__error-message {
      padding: 2px 8px;
      color: $white;
      font-size: 0.7143em;
      background: $red-light;
      border-radius: 1.6em;
    }
  }

  &__login-btn {
    width: 100%;
  }

  &__signup {
    width: 100%;
    padding-top: 30px;
    margin-top: 30px;
    border-top: 1px solid $white;

    &__item {
      + .login-section__signup__item {
        margin-top: 8px;
      }
    }
  }

  &:not(.login-section--mode-edit) {
    .login-section__input-area {
      &__email-field {
        border-color: transparent;
        color: $green-light;
        background-color: rgba(0, 0, 0, 50%);

        &::placeholder {
          color: $white;
          opacity: 0.4;
        }

        &--validation-error {
          color: $red-light;
          border-color: rgba(255, 46, 0, 0.3);
        }
      }
    }
  }

  &--mode-edit {
    .login-section__input-area {
      &__forgot,
      &__signup {
        &__link {
          color: $black20;
        }
      }
    }
  }

  &__buttons {
    margin-top: 24px;
  }
}
</style>

<style lang="scss">
.login-section {
  &__input {
    &-item {
      + .login-section__input-item {
        margin-top: 8px;
      }
    }

    &-area {
      .login-section__input-area__pw-field {
        background-color: unset;
        backdrop-filter: unset;

        .atom-input-password {
          backdrop-filter: blur(12px);

          .login-section:not(.login-section--mode-edit) & {
            border-color: transparent;
            background-color: rgba(0, 0, 0, 50%);
            color: $green-light;

            &::placeholder {
              color: $white;
              opacity: 0.4;
            }
          }
        }

        &--validation-error {
          border-color: unset;

          .atom-input-password {
            color: $red-light;
            border-color: rgba(255, 46, 0, 0.3);
          }
        }

        .password-view-change-parts {
          &__button {
            opacity: 1;

            svg path {
              fill: rgba(#fff, 50%);
            }

            &--text {
              background: rgba(#fff, 20%);
            }
          }
        }
      }

      &__forgot {
        margin-top: 24px;
      }
    }
  }

  &__login-btn {
    width: 100%;

    &[disabled] {
      opacity: 0.4 !important;
    }
  }

  &--mode-edit {
    .login-section__login-btn[disabled] {
      color: $green-light !important;
      background: $black !important;
      border-color: $black !important;
    }
  }
}
</style>
